import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Upload } from 'lucide-react';
import Header from '@/components/Header';
import { useAuth } from '@/contexts/AuthContext';
import { recipeService } from '@/services/recipeService';
import { useToast } from '@/hooks/use-toast';
import { Difficulty, MealCategory, Tag } from '@/types';

const AddRecipe = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { token, isLoggedIn } = useAuth();
    const [loading, setLoading] = useState(false);

    // Estado inicial con valores por defecto válidos
    const [formData, setFormData] = useState({
        title: '',
        category: '' as MealCategory,
        time: '',
        difficulty: '' as Difficulty,
        calories: '',
        protein: '',
        carbs: '',
        fat: '',
        instructions: ''
    });

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [ingredients, setIngredients] = useState<string[]>(['']);
    const [tags, setTags] = useState<Tag[]>([]);
    const [isPublic, setIsPublic] = useState(false);

    // Redirigir si no está loggeado
    React.useEffect(() => {
        if (!isLoggedIn) {
            navigate('/login');
        }
    }, [isLoggedIn, navigate]);

    const addIngredient = () => {
        setIngredients([...ingredients, '']);
    };

    const updateIngredient = (index: number, value: string) => {
        const updated = [...ingredients];
        updated[index] = value;
        setIngredients(updated);
    };

    const removeIngredient = (index: number) => {
        if (ingredients.length > 1) {
            setIngredients(ingredients.filter((_, i) => i !== index));
        }
    };

    const addTag = (tag: Tag) => {
        if (!tags.includes(tag)) {
            setTags([...tags, tag]);
        }
    };

    const removeTag = (tagToRemove: Tag) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validar tipo de archivo
            if (!file.type.startsWith('image/')) {
                toast({
                    title: "Error",
                    description: "Por favor selecciona un archivo de imagen",
                    variant: "destructive",
                });
                return;
            }

            // Validar tamaño (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast({
                    title: "Error",
                    description: "La imagen es demasiado grande (máximo 10MB)",
                    variant: "destructive",
                });
                return;
            }

            setImageFile(file);

            // Crear preview
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Función de validación mejorada
    const validateForm = (): string | null => {
        if (!formData.title.trim()) {
            return "El título es requerido";
        }
        if (!formData.category) {
            return "La categoría es requerida";
        }
        if (!formData.time.trim()) {
            return "El tiempo de cocción es requerido";
        }
        if (!formData.difficulty) {
            return "La dificultad es requerida";
        }
        const calories = parseInt(formData.calories);
        const protein = parseInt(formData.protein);
        const carbs = parseInt(formData.carbs);
        const fat = parseInt(formData.fat);

        if (!formData.calories || calories <= 0) {
            return "Las calorías son requeridas y deben ser mayores que 0";
        }
        if (!formData.protein || protein <= 0) {
            return "La proteína es requerida y debe ser mayor que 0";
        }
        if (!formData.carbs || carbs <= 0) {
            return "Los carbohidratos son requeridos y deben ser mayores que 0";
        }
        if (!formData.fat || fat <= 0) {
            return "La grasa es requerida y debe ser mayor que 0";
        }

        const validIngredients = ingredients.filter(ing => ing.trim());
        if (validIngredients.length === 0) {
            return "Agrega al menos un ingrediente";
        }

        return null; // No hay errores
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            toast({
                title: "Error",
                description: "Debes iniciar sesión para crear recetas",
                variant: "destructive",
            });
            return;
        }

        // Validar formulario
        const validationError = validateForm();
        if (validationError) {
            toast({
                title: "Error de validación",
                description: validationError,
                variant: "destructive",
            });
            return;
        }

        setLoading(true);

        try {
            // Preparar datos para enviar
            const recipePayload = {
                title: formData.title.trim(),
                time: formData.time.trim(),
                difficulty: formData.difficulty,
                category: formData.category,
                calories: parseInt(formData.calories),
                protein: parseInt(formData.protein),
                carbs: parseInt(formData.carbs),
                fat: parseInt(formData.fat),
                ingredients: ingredients.filter(ing => ing.trim()),
                tags: tags,
                instructions: formData.instructions.trim(),
                isPublic: isPublic
            };

            console.log('Enviando receta:', recipePayload);

            await recipeService.createRecipe(recipePayload, token, imageFile || undefined);

            toast({
                title: "¡Éxito!",
                description: "Receta creada correctamente",
            });

            navigate('/');

        } catch (error: any) {
            console.error('Error creating recipe:', error);
            toast({
                title: "Error",
                description: error.message || "Error al crear la receta",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    // Lista de tags disponibles
    const availableTags: Tag[] = [
        Tag.VEGETARIANA,
        Tag.VEGANA,
        Tag.ALTA_PROTEINA,
        Tag.BAJO_EN_CARBOS,
        Tag.GLUTEN_FREE,
        Tag.SIN_LACTOSA,
        Tag.RÁPIDA,
        Tag.KETO,
        Tag.ALTA_FIBRA,
        Tag.BAJO_EN_GRASAS,
        Tag.BAJO_EN_CALORIAS
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="flex items-center gap-4 mb-6">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Volver al Inicio
                    </Button>
                    <h1 className="text-3xl font-bold text-food-purple">Agregar Nueva Receta</h1>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid gap-6 md:grid-cols-2">
                        <Card>
                            <CardHeader>
                                <CardTitle>Información Básica</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="title">Título de la Receta *</Label>
                                    <Input
                                        id="title"
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Ingresa el título de la receta"
                                        required
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="image">Imagen de la Receta</Label>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-4">
                                            <Input
                                                id="image"
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => document.getElementById('image')?.click()}
                                                className="flex items-center gap-2"
                                            >
                                                <Upload className="h-4 w-4" />
                                                {imageFile ? 'Cambiar Imagen' : 'Subir Imagen'}
                                            </Button>
                                        </div>
                                        {imagePreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imagePreview}
                                                    alt="Vista previa"
                                                    className="h-32 w-32 object-cover rounded-lg border"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {imageFile?.name} ({(imageFile?.size || 0 / 1024 / 1024).toFixed(2)} MB)
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="category">Categoría *</Label>
                                    <Select
                                        value={formData.category}
                                        onValueChange={(value: MealCategory) => setFormData(prev => ({ ...prev, category: value }))}
                                        required
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Selecciona categoría" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={MealCategory.DESAYUNO}>Desayuno</SelectItem>
                                            <SelectItem value={MealCategory.COMIDA}>Comida</SelectItem>
                                            <SelectItem value={MealCategory.CENA}>Cena</SelectItem>
                                            <SelectItem value={MealCategory.SNACK}>Snack</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="time">Tiempo de Cocción *</Label>
                                        <Input
                                            id="time"
                                            value={formData.time}
                                            onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                                            placeholder="ej., 30 minutos"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="difficulty">Dificultad *</Label>
                                        <Select
                                            value={formData.difficulty}
                                            onValueChange={(value: Difficulty) => setFormData(prev => ({ ...prev, difficulty: value }))}
                                            required
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Selecciona dificultad" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={Difficulty.FÁCIL}>Fácil</SelectItem>
                                                <SelectItem value={Difficulty.MEDIO}>Medio</SelectItem>
                                                <SelectItem value={Difficulty.DIFÍCIL}>Difícil</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="instructions">Instrucciones de Preparación</Label>
                                    <Textarea
                                        id="instructions"
                                        value={formData.instructions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                                        placeholder="Describe los pasos para preparar la receta..."
                                        rows={4}
                                    />
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="isPublic"
                                        checked={isPublic}
                                        onChange={(e) => setIsPublic(e.target.checked)}
                                        className="rounded border-gray-300"
                                    />
                                    <Label htmlFor="isPublic" className="text-sm">
                                        Hacer receta pública (visible para todos los usuarios)
                                    </Label>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Información Nutricional *</CardTitle>
                                <CardDescription>Por porción</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="calories">Calorías *</Label>
                                        <Input
                                            id="calories"
                                            type="number"
                                            min="0"
                                            value={formData.calories}
                                            onChange={(e) => setFormData(prev => ({ ...prev, calories: e.target.value }))}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="protein">Proteína (g) *</Label>
                                        <Input
                                            id="protein"
                                            type="number"
                                            min="0"
                                            value={formData.protein}
                                            onChange={(e) => setFormData(prev => ({ ...prev, protein: e.target.value }))}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="carbs">Carbohidratos (g) *</Label>
                                        <Input
                                            id="carbs"
                                            type="number"
                                            min="0"
                                            value={formData.carbs}
                                            onChange={(e) => setFormData(prev => ({ ...prev, carbs: e.target.value }))}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="fat">Grasa (g) *</Label>
                                        <Input
                                            id="fat"
                                            type="number"
                                            min="0"
                                            value={formData.fat}
                                            onChange={(e) => setFormData(prev => ({ ...prev, fat: e.target.value }))}
                                            placeholder="0"
                                            required
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Ingredientes *</CardTitle>
                                <CardDescription>Agrega al menos un ingrediente</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {ingredients.map((ingredient, index) => (
                                    <div key={index} className="flex gap-2">
                                        <Input
                                            value={ingredient}
                                            onChange={(e) => updateIngredient(index, e.target.value)}
                                            placeholder={`Ingrediente ${index + 1}`}
                                            required={index === 0}
                                        />
                                        {ingredients.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={() => removeIngredient(index)}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={addIngredient}
                                    className="w-full"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Agregar Ingrediente
                                </Button>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Etiquetas</CardTitle>
                                <CardDescription>Categoriza tu receta</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex flex-wrap gap-2">
                                    {availableTags.map((tag) => (
                                        <Badge
                                            key={tag}
                                            variant={tags.includes(tag) ? "default" : "outline"}
                                            className="cursor-pointer"
                                            onClick={() => tags.includes(tag) ? removeTag(tag) : addTag(tag)}
                                        >
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {tags.length > 0 ? (
                                        <div>
                                            <strong>Etiquetas seleccionadas:</strong>{' '}
                                            {tags.join(', ')}
                                        </div>
                                    ) : (
                                        'Haz clic en las etiquetas para seleccionarlas'
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <div className="md:col-span-2">
                            <Button
                                type="submit"
                                className="w-full bg-food-purple hover:bg-food-purple/90"
                                size="lg"
                                disabled={loading}
                            >
                                {loading ? 'Creando Receta...' : 'Guardar Receta'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddRecipe;